import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { LogOut, CheckCircle2, AlertTriangle, Wallet, History, Mail, Copy, Send, Upload } from "lucide-react";

import { fmtDateStr } from "../lib/datetime";

const MARITAL = ["Single", "Married", "Divorced", "Widowed", "Separated"];
const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Unknown"];
const RELATIONS = ["Father", "Mother", "Spouse", "Sibling", "Friend", "Child", "Other"];

const fmtINR = (n) => "₹ " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function MyOnboarding() {
  const { user, logout } = useAuth();
  const [d, setD] = useState(null);
  const [decline, setDecline] = useState(false);
  const [counter, setCounter] = useState("");
  const [form, setForm] = useState({});
  const [photoBusy, setPhotoBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/my-onboarding");
      setD(data);
      setForm(data.candidate_form || {});
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  useEffect(() => { refresh(); }, []);

  if (!d) return <div className="min-h-screen bg-slate-50 grid place-items-center text-slate-400 text-sm">Loading…</div>;

  const stage = d.stage;
  const requiresSpouse = ["Married", "Divorced", "Widowed", "Separated"].includes(form.marital_status);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveDraft = async () => {
    try {
      await api.post("/my-onboarding/save-form", form);
      toast.success("Saved");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const submit = async () => {
    const required = ["father_name", "gender", "marital_status", "permanent_address", "blood_group",
      "pan_number", "aadhaar_number", "date_of_birth", "personal_whatsapp",
      "account_number", "ifsc_code",
      "emergency_contact_name", "emergency_relation", "emergency_contact_number"];
    for (const r of required) {
      if (!form[r] || !String(form[r]).trim()) { toast.error(`${r.replace(/_/g, " ")} is required`); return; }
    }
    if (requiresSpouse && !form.spouse_name?.trim()) { toast.error("spouse name is required"); return; }
    if (form.same_as_permanent === false && !form.correspondence_address?.trim()) { toast.error("correspondence address is required"); return; }
    if (!form.photo_gridfs_id) { toast.error("Please upload your photo"); return; }
    try {
      await api.post("/my-onboarding/save-form", form);
      await api.post("/my-onboarding/submit-form");
      toast.success("Submitted! HR will verify and revert.");
      await logout();
      window.location.href = "/login";
    } catch (e) { toast.error(formatError(e)); }
  };

  const acceptOffer = async () => {
    try {
      await api.post("/my-onboarding/accept-offer", { accepted: true });
      toast.success("Offer accepted — fill the form below");
      refresh();
    } catch (e) { toast.error(formatError(e)); }
  };
  const declineOffer = async () => {
    if (!counter) return toast.error("Enter your expected salary");
    try {
      const { data } = await api.post("/my-onboarding/accept-offer", { accepted: false, counter_offer_amount: Number(counter) });
      if (data.out_of_budget) {
        // Only triggered if HR had already marked the offer FINAL → access is revoked.
        toast.error("Offer was marked final. Access revoked.");
        await logout();
        window.location.href = "/login";
        return;
      }
      // First-time decline / counter-offer — DO NOT log the candidate out.
      // They stay on the portal and see the "Waiting for HR" view.
      toast.success("Your expected salary has been sent to HR. You'll be notified.");
      setDecline(false);
      setCounter("");
      await refresh();
    } catch (e) { toast.error(formatError(e)); }
  };

  const rejectFinalOffer = async () => {
    if (!window.confirm(
      "Reject the FINAL offer? Your candidate access will be revoked immediately and you'll be marked Out of Budget."
    )) return;
    try {
      await api.post("/my-onboarding/accept-offer", { accepted: false });
      toast.error("Final offer rejected. Access revoked.");
      await logout();
      window.location.href = "/login";
    } catch (e) { toast.error(formatError(e)); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      // Backend response: { ok: true, id: "<gridfs_id>" }. We merge ONLY the new
      // photo id into local form state — NEVER call refresh() here, because that
      // would overwrite the user's typed-but-unsaved fields with whatever the
      // server has (which doesn't yet include their pending edits).
      const { data } = await api.post(
        "/my-onboarding/photo", fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setForm((f) => ({
        ...f,
        photo_gridfs_id: data?.id || f.photo_gridfs_id,
        photo_uploaded_at: new Date().toISOString(),
      }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(formatError(e)); } finally { setPhotoBusy(false); }
  };

  const photoUrl = form.photo_gridfs_id ? `${process.env.REACT_APP_BACKEND_URL}/api/onboardings/${d.id}/candidate-photo?_=${Date.now()}` : "";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[var(--pk-accent)] grid place-items-center text-white font-bold heading text-sm">PV</div>
            <div>
              <div className="heading font-bold text-[15px] leading-tight">Panchakosh Ventures</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Candidate Portal</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={async () => { await logout(); window.location.href = "/login"; }} data-testid="myonb-logout">
            <LogOut size={14} className="mr-1.5" /> Sign out
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6" data-testid="my-onboarding-page">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1">Welcome</div>
          <h1 className="heading text-3xl font-extrabold tracking-tight">{d.interviewee_name}</h1>
          <p className="text-slate-600 text-sm mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Position: <b>{d.position}</b></span>
            <span>· Company: <b>{d.company || "—"}</b></span>
            {d.temp_employee_id && (
              <Badge className="bg-violet-100 text-violet-800 mono text-[10.5px] font-bold" data-testid="myonb-tempid">
                {d.temp_employee_id}
              </Badge>
            )}
          </p>
        </div>

        {/* Stage 1: Offer */}
        {(stage === "offer_sent" || stage === "counter_offered") && (
          <div className={`pk-card p-6 border-2 relative overflow-hidden ${d.offer_final ? "border-rose-300 bg-rose-50/40" : "border-violet-200"}`}>
            {/* Big FINAL OFFER stamp */}
            {d.offer_final && (
              <div
                className="pointer-events-none absolute -top-3 -right-6 sm:top-4 sm:right-6 rotate-[-8deg] border-[3px] border-rose-600 text-rose-700 font-extrabold tracking-[0.18em] px-4 py-1.5 text-[18px] sm:text-[22px] uppercase bg-white/80 shadow-md"
                style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                data-testid="myonb-final-stamp"
              >
                Final Offer
              </div>
            )}
            <h2 className="heading text-xl font-bold mb-2 flex items-center gap-2">
              <Wallet size={18} /> Your Offer
              {d.offer_final && (
                <Badge className="bg-rose-100 text-rose-800 ml-1 text-[10px]" data-testid="myonb-final-badge">FINAL</Badge>
              )}
            </h2>
            <div className="text-3xl font-extrabold mono text-violet-700 mb-1" data-testid="myonb-offer-amount">
              {fmtINR(d.offered_salary)} <span className="text-sm font-normal text-slate-500">per month</span>
            </div>
            <div className="text-[12.5px] text-slate-600 mb-4">Joining Date: <b>{fmtDateStr(d.joining_date, "—")}</b></div>

            {d.offer_final ? (
              <div className="space-y-3" data-testid="myonb-final-block">
                <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-[13px] text-amber-900 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold mb-1">This is your FINAL offer.</div>
                      <p data-testid="myonb-final-message">
                        If your performance in the <b>next 4 months</b> is better, you will be liable
                        to request a <b>Salary Hike</b>. Counter-offers are no longer available at
                        this stage.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={acceptOffer} className="bg-emerald-600 hover:bg-emerald-700" data-testid="myonb-final-accept">
                    <CheckCircle2 size={14} className="mr-1.5" /> Accept Final Offer
                  </Button>
                  <Button variant="outline" onClick={rejectFinalOffer} className="text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="myonb-final-reject">
                    Reject Final Offer
                  </Button>
                </div>
                <div className="text-[11px] text-slate-500 italic">
                  Rejecting will revoke your candidate access immediately and mark you as <i>Out of Budget</i>.
                </div>
              </div>
            ) : stage === "counter_offered" ? (
              <div className="space-y-3" data-testid="myonb-waiting-block">
                <div className="rounded-md border border-violet-200 bg-violet-50/60 p-4 text-[13px] text-violet-900 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-violet-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-bold mb-1">Waiting for HR</div>
                      <p>
                        You requested <b className="mono">{fmtINR(d.candidate_counter_offer)}</b> per month.
                        HR has been notified and will revert shortly. You don't need to log out — keep
                        this page open and refresh when you hear from HR.
                      </p>
                    </div>
                  </div>
                </div>
                <Button variant="outline" onClick={refresh} className="h-9" data-testid="myonb-waiting-refresh">
                  Refresh
                </Button>
              </div>
            ) : !decline ? (
              <div className="flex gap-2">
                <Button onClick={acceptOffer} className="bg-emerald-600 hover:bg-emerald-700" data-testid="myonb-accept">
                  <CheckCircle2 size={14} className="mr-1.5" /> Accept Offer
                </Button>
                <Button variant="outline" onClick={() => setDecline(true)} className="text-rose-700 border-rose-300" data-testid="myonb-decline">
                  Decline / Negotiate
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Your Expected Monthly Salary (₹) *</Label>
                <Input type="number" value={counter} onChange={(e) => setCounter(e.target.value)} placeholder="e.g. 55000" data-testid="myonb-counter" />
                <div className="flex gap-2">
                  <Button onClick={declineOffer} className="bg-amber-600 hover:bg-amber-700" data-testid="myonb-counter-submit">Send to HR</Button>
                  <Button variant="outline" onClick={() => { setDecline(false); setCounter(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Offer history — visible only during/after negotiation */}
        {(stage === "offer_sent" || stage === "counter_offered") && (d.offer_history || []).length > 0 && (
          <OfferHistoryCard history={d.offer_history || []} />
        )}

        {/* Stage 2: Filling */}
        {stage === "candidate_filling" && (
          <>
            <div className="pk-card p-6">
              <h2 className="heading text-xl font-bold mb-3">Step 2 — Your Information</h2>
              <p className="text-[12px] text-slate-600 mb-4">Please complete all fields. HR will verify each item.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label="Father's Name *"><Input value={form.father_name || ""} onChange={(e) => upd("father_name", e.target.value)} data-testid="cf-father" /></F>
                <F label="Gender *">
                  <Select value={form.gender || ""} onValueChange={(v) => upd("gender", v)}>
                    <SelectTrigger data-testid="cf-gender"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{["Male", "Female", "Other", "Prefer not to say"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Date of Birth *"><Input type="date" value={form.date_of_birth || ""} onChange={(e) => upd("date_of_birth", e.target.value)} data-testid="cf-dob" /></F>
                <F label="Marital Status *">
                  <Select value={form.marital_status || ""} onValueChange={(v) => upd("marital_status", v)}>
                    <SelectTrigger data-testid="cf-marital"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{MARITAL.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                {requiresSpouse && <F label="Spouse Name *"><Input value={form.spouse_name || ""} onChange={(e) => upd("spouse_name", e.target.value)} data-testid="cf-spouse" /></F>}
                <F label="Blood Group *">
                  <Select value={form.blood_group || ""} onValueChange={(v) => upd("blood_group", v)}>
                    <SelectTrigger data-testid="cf-blood"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{BLOOD.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Personal WhatsApp *"><Input value={form.personal_whatsapp || ""} onChange={(e) => upd("personal_whatsapp", e.target.value)} data-testid="cf-whatsapp" /></F>
                <F label="PAN Number *"><Input value={form.pan_number || ""} onChange={(e) => upd("pan_number", e.target.value.toUpperCase())} className="mono uppercase" data-testid="cf-pan" /></F>
                <F label="Aadhaar Number *"><Input value={form.aadhaar_number || ""} onChange={(e) => upd("aadhaar_number", e.target.value.replace(/\D/g, ""))} maxLength={12} className="mono" data-testid="cf-aadhaar" /></F>
                <F label="Passport No"><Input value={form.passport_no || ""} onChange={(e) => upd("passport_no", e.target.value.toUpperCase())} className="mono uppercase" /></F>
                <F label="Voter ID"><Input value={form.voter_id || ""} onChange={(e) => upd("voter_id", e.target.value.toUpperCase())} className="mono uppercase" /></F>
                <F label="Account Number *"><Input value={form.account_number || ""} onChange={(e) => upd("account_number", e.target.value.replace(/\D/g, ""))} className="mono" data-testid="cf-account" /></F>
                <F label="IFSC Code *"><Input value={form.ifsc_code || ""} onChange={(e) => upd("ifsc_code", e.target.value.toUpperCase())} className="mono uppercase" data-testid="cf-ifsc" /></F>

                <div className="md:col-span-2">
                  <F label="Permanent Address *"><Textarea value={form.permanent_address || ""} onChange={(e) => upd("permanent_address", e.target.value)} rows={2} data-testid="cf-perm-address" /></F>
                </div>
                <div className="md:col-span-2">
                  <label className="inline-flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer">
                    <Checkbox checked={form.same_as_permanent !== false} onCheckedChange={(c) => upd("same_as_permanent", c === true)} data-testid="cf-same-address" />
                    Correspondence address is same as permanent
                  </label>
                </div>
                {form.same_as_permanent === false && (
                  <div className="md:col-span-2">
                    <F label="Correspondence Address *"><Textarea value={form.correspondence_address || ""} onChange={(e) => upd("correspondence_address", e.target.value)} rows={2} data-testid="cf-corr-address" /></F>
                  </div>
                )}

                <F label="Emergency Contact Name *"><Input value={form.emergency_contact_name || ""} onChange={(e) => upd("emergency_contact_name", e.target.value)} data-testid="cf-em-name" /></F>
                <F label="Relation *">
                  <Select value={form.emergency_relation || ""} onValueChange={(v) => upd("emergency_relation", v)}>
                    <SelectTrigger data-testid="cf-em-rel"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>{RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </F>
                <F label="Emergency Contact Number *"><Input value={form.emergency_contact_number || ""} onChange={(e) => upd("emergency_contact_number", e.target.value)} data-testid="cf-em-phone" /></F>
              </div>

              {/* Photo */}
              <div className="border-t border-slate-100 pt-4 mt-4">
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Profile Photo *</Label>
                <div className="flex items-start gap-3 mt-1.5 flex-wrap">
                  {photoUrl ? <img src={photoUrl} alt="" className="w-24 h-24 rounded-md object-cover border" data-testid="cf-photo-preview" /> : <div className="w-24 h-24 rounded-md bg-slate-100 grid place-items-center text-slate-400 border border-dashed">No photo</div>}
                  <label className="inline-flex items-center cursor-pointer">
                    <Button asChild disabled={photoBusy} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] h-9">
                      <span><Upload size={13} className="mr-1.5" /> {photoBusy ? "Uploading…" : "Upload Photo"}</span>
                    </Button>
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = ""; }} data-testid="cf-photo-input" />
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={saveDraft} data-testid="cf-save">Save Draft</Button>
                <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cf-submit">
                  <CheckCircle2 size={14} className="mr-1.5" /> Submit for Verification
                </Button>
              </div>
            </div>

            {/* Submit Documents via Email + Draft Offer Letter view */}
            <CandidateDocsViaEmail d={d} />
          </>
        )}

        {/* Submitted / Verified / Contract stages — read-only message */}
        {["submitted", "verified", "contract_sent", "contract_confirmed", "enrolled"].includes(stage) && (
          <div className="pk-card p-6 border-2 border-emerald-200">
            <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2"><CheckCircle2 size={18} /> Submitted</div>
            <p className="text-[13px] text-slate-700">Thank you. HR will review your information and revert with the contract and next steps. You can close this page.</p>
          </div>
        )}

        {/* Counter offered — awaiting HR */}
        {stage === "counter_offered" && (
          <div className="pk-card p-6 border-2 border-amber-200">
            <div className="flex items-center gap-2 text-amber-800 font-semibold mb-2"><AlertTriangle size={18} /> Counter Offer Sent</div>
            <p className="text-[13px] text-slate-700">Your counter offer has been sent to HR. We'll be in touch with the decision.</p>
          </div>
        )}

        {stage === "out_of_budget" && (
          <div className="pk-card p-6 border-2 border-rose-200">
            <div className="flex items-center gap-2 text-rose-800 font-semibold mb-2"><AlertTriangle size={18} /> Offer Closed</div>
            <p className="text-[13px] text-slate-700">Thank you for your time. Unfortunately the offer is no longer available.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function OfferHistoryCard({ history }) {
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  // Show oldest → newest so the candidate reads the negotiation as a story
  const ordered = [...history].sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  const meta = (h) => {
    switch (h.type) {
      case "initial_offer":
        return { label: "Initial Offer from HR", side: "hr", color: "violet" };
      case "candidate_counter":
        return { label: "Your Counter Offer", side: "you", color: "amber" };
      case "hr_counter":
        return { label: "HR Counter Offer", side: "hr", color: "violet" };
      case "hr_accepted_candidate":
        return { label: "HR Accepted Your Counter", side: "hr", color: "emerald" };
      case "final_offer":
        return { label: "FINAL Offer from HR", side: "hr", color: "rose" };
      case "candidate_accepted":
        return { label: "You Accepted the Offer", side: "you", color: "emerald" };
      case "offer_reopened":
        return { label: "Offer Reopened by Admin", side: "hr", color: "violet" };
      default:
        return { label: h.type, side: "hr", color: "slate" };
    }
  };
  const pillCls = {
    violet: "bg-violet-100 text-violet-800 border-violet-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rose: "bg-rose-100 text-rose-800 border-rose-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <div className="pk-card p-5" data-testid="myonb-offer-history">
      <h3 className="heading text-base font-bold flex items-center gap-2 mb-3">
        <History size={15} /> Negotiation History
        <span className="text-[11px] font-normal text-slate-500">({ordered.length} {ordered.length === 1 ? "round" : "rounds"})</span>
      </h3>
      <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3">
        {ordered.map((h, i) => {
          const m = meta(h);
          const isYou = m.side === "you";
          return (
            <li
              key={i}
              className={`pl-4 ${isYou ? "ml-auto" : ""}`}
              data-testid={`myonb-offer-history-row-${i}`}
            >
              <span
                className={`absolute -left-[7px] w-3 h-3 rounded-full border-2 border-white ${
                  m.color === "rose" ? "bg-rose-500" :
                  m.color === "amber" ? "bg-amber-500" :
                  m.color === "emerald" ? "bg-emerald-500" :
                  m.color === "violet" ? "bg-violet-500" :
                  "bg-slate-400"
                }`}
              />
              <div className="text-[11px] text-slate-500">{fmt(h.at)}</div>
              <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                <span className={`text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${pillCls[m.color]}`}>
                  {m.label}
                </span>
                <span className="font-extrabold mono text-slate-800 text-[15px]" data-testid={`myonb-offer-amount-${i}`}>
                  ₹ {Number(h.amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
                <span className="text-[10.5px] text-slate-500">/mo</span>
              </div>
              {h.note && (
                <div className="text-[11.5px] text-slate-600 italic mt-1">&ldquo;{h.note}&rdquo;</div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}


function CandidateDocsViaEmail({ d }) {
  const HR_EMAIL_LOCAL = "MyHr@techknowskola.com";
  const tempId = d.temp_employee_id || "[Temp Employee ID]";
  const subject = `Acceptance to Offer Letter for ${d.interviewee_name || "[Your Name]"} & Temp Employee ID ${tempId}`;
  const body = `Dear HR Team,

I confirm my acceptance of the offer letter for the position of ${d.position || "[Position]"}.

My Temp Employee ID is: ${tempId}

Here is the requested Documents (attached):
- Aadhaar Card
- PAN Card
- Academic Certificates and Mark Sheets
- Previous Employment Records (if applicable)
- Bank Account Proof (Cancelled Cheque / Passbook)
- Passport Size Photograph

Date of Joining: ${fmtDateStr(d.joining_date, "[Joining Date]")}

Kindly let me know if anything else is required from my end.

Warm Regards,
${d.interviewee_name || "[Your Name]"}`;

  const offerLetter = `Dear ${d.interviewee_name || "[Your Name]"},

Greetings from Panchakosh Ventures - A Group of Company.

We are pleased to inform you that you have successfully cleared the interview process, and we are delighted to welcome you to Our family.

Temp Employee ID: ${tempId}
Your Date of Joining (DOJ) is: ${fmtDateStr(d.joining_date, "[Joining Date]")}
Offered Monthly Salary: ${fmtINR(d.offered_salary)}

Please email the following documents to ${HR_EMAIL_LOCAL}:
- Aadhaar Card (Identity & Address Proof)
- PAN Card
- Academic Certificates and Mark Sheets
- Previous Employment Records (if applicable)
- Bank Account Proof (Cancelled Cheque / Passbook Copy)
- Passport Size Photograph

Subject line to use:
"Acceptance to Offer Letter for ${d.interviewee_name || "[Your Name]"} & Temp Employee ID ${tempId}"

Warm Regards,
HR Department — Panchakosh Ventures`;

  const sendEmail = () => {
    const url = `mailto:${HR_EMAIL_LOCAL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
  };

  return (
    <>
      {/* Drafted Offer Letter — read-only view */}
      <div className="pk-card p-6" data-testid="myonb-offer-letter-view">
        <h2 className="heading text-xl font-bold mb-2 flex items-center gap-2">
          <Mail size={18} /> Your Offer Letter
        </h2>
        <p className="text-[12px] text-slate-600 mb-3">
          Keep this letter handy. Email your documents to the HR address shown below.
        </p>
        <pre className="text-[12px] bg-slate-50 border border-slate-200 rounded p-4 whitespace-pre-wrap font-sans max-h-[320px] overflow-y-auto" data-testid="myonb-offer-letter-body">
          {offerLetter}
        </pre>
      </div>

      {/* Submit Documents via Email */}
      <div className="pk-card p-6 border-2 border-violet-200" data-testid="myonb-docs-email">
        <h2 className="heading text-xl font-bold mb-2 flex items-center gap-2">
          <Send size={18} /> Submit Your Documents via Email
        </h2>
        <p className="text-[12.5px] text-slate-700 mb-3">
          Email your required onboarding documents to the HR address below. Use the
          pre-filled subject so we can route your acceptance correctly.
        </p>
        <div className="space-y-2 mb-3">
          <div className="text-[12px]">
            <span className="text-slate-500 font-semibold uppercase text-[10.5px] tracking-wider mr-2">To</span>
            <span className="mono font-bold text-violet-700" data-testid="myonb-hr-email">{HR_EMAIL_LOCAL}</span>
          </div>
          <div className="text-[12px]">
            <span className="text-slate-500 font-semibold uppercase text-[10.5px] tracking-wider mr-2">Subject</span>
            <span data-testid="myonb-docs-subject">{subject}</span>
          </div>
        </div>
        <pre className="text-[11.5px] bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap font-sans max-h-[200px] overflow-y-auto" data-testid="myonb-docs-body">
          {body}
        </pre>
        <div className="flex flex-wrap gap-2 mt-3">
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(HR_EMAIL_LOCAL).then(() => toast.success("HR email copied"))} className="h-8 text-[11px]" data-testid="myonb-copy-email">
            <Copy size={11} className="mr-1" /> Copy Email
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(subject).then(() => toast.success("Subject copied"))} className="h-8 text-[11px]" data-testid="myonb-copy-subject">
            <Copy size={11} className="mr-1" /> Copy Subject
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(body).then(() => toast.success("Body copied"))} className="h-8 text-[11px]" data-testid="myonb-copy-body">
            <Copy size={11} className="mr-1" /> Copy Body
          </Button>
          <Button size="sm" onClick={sendEmail} className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700" data-testid="myonb-send-email">
            <Send size={11} className="mr-1" /> Send Email
          </Button>
        </div>
      </div>
    </>
  );
}


function F({ label, children }) {
  return <div><Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
